import { Funnel, FunnelFilters } from '@/types/funnel.ts';

const FILTERS_STORAGE_KEY = 'meta:flow:filters';

const DEFAULT_FILTERS: FunnelFilters = {
    sortBy: 'created_at',
    sortOrder: 'desc',
};

/**
 * Filtros aplicados na listagem sobrevivem à ida e volta ao builder.
 * O botão "Voltar" do builder faz navigate('/meta/flow'), que descarta
 * qualquer estado da rota — então a persistência precisa ficar fora dela.
 * sessionStorage e não localStorage: o filtro vale para a aba aberta, não
 * para sempre.
 */
export function loadFunnelFilters(): FunnelFilters {
    try {
        const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
        if (!raw) return { ...DEFAULT_FILTERS };
        const parsed = JSON.parse(raw) as FunnelFilters;
        return { ...DEFAULT_FILTERS, ...parsed };
    } catch {
        return { ...DEFAULT_FILTERS };
    }
}

export function saveFunnelFilters(filters: FunnelFilters): void {
    try {
        sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
        // sessionStorage indisponível (aba anônima, storage bloqueado) —
        // a tela segue funcionando, só não lembra o filtro.
    }
}

/**
 * Ordenação é o padrão da tela, não um filtro — só conta o que de fato
 * esconde funis da listagem.
 */
export function hasActiveFunnelFilters(filters: FunnelFilters): boolean {
    return Boolean(filters.page || filters.status || filters.categoryId != null);
}

/**
 * Filtra funis por página, status e categoria
 */
export function filterFunnels(
    funnels: Funnel[],
    page?: string,
    status?: 'active' | 'inactive',
    categoryId?: number
): Funnel[] {
    return funnels.filter(funnel => {
        // Filtro por página
        if (page && !funnel.pages.some(p => p.page_id === page)) {
            return false;
        }

        // Filtro por status
        if (status === 'active' && !funnel.is_active) {
            return false;
        }
        if (status === 'inactive' && funnel.is_active) {
            return false;
        }

        // Filtro por categoria
        if (categoryId != null && funnel.internal_category_id !== categoryId) {
            return false;
        }

        return true;
    });
}

/**
 * Ordena funis
 */
export function sortFunnels(
    funnels: Funnel[],
    sortBy: 'created_at' | 'nome',
    sortOrder: 'asc' | 'desc'
): Funnel[] {
    const sorted = [...funnels].sort((a, b) => {
        let comparison = 0;

        if (sortBy === 'created_at') {
            comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        } else if (sortBy === 'nome') {
            comparison = a.nome.localeCompare(b.nome, 'pt-BR');
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
}

/**
 * Formata data para exibição
 */
export function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
}

/**
 * Formata data relativa (ex: "há 2 horas")
 */
export function formatRelativeDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return 'agora mesmo';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
        return `há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
        return `há ${diffInWeeks} ${diffInWeeks === 1 ? 'semana' : 'semanas'}`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    return `há ${diffInMonths} ${diffInMonths === 1 ? 'mês' : 'meses'}`;
}